import { useContext, useEffect, useRef } from 'react';

import { AuthContext } from '../../providers/AuthProvider';
import { ApiMethod } from '../models/api-types.type';
import { routes } from '../../assets/constants';

/**
 * The team tag for "open in visualizer" links.
 *
 * The API signs it for the signed-in user: Ardealy staff get one that covers
 * every vendor, a vendor's user one for their own vendor. `buildVisualizerUrl`
 * adds it to preview links, the visualizer sends it with every call, and the
 * API keeps those visits apart from real visitors' — whatever the device or
 * browser. Kept in localStorage so links drawn right after a reload carry it.
 */
export const PREVIEW_TAG_KEY = 'ardealy.bo.preview.tag';

/** Stop handing out a tag this long before it expires, so an opened link has time left. */
const MARGIN_MS = 60 * 60 * 1000;
/** Fetch a fresh one this often while the backoffice is open. */
const REFRESH_MS = 4 * 60 * 60 * 1000;

interface StoredTag {
  token: string;
  expiresAt: string;
}

export function getPreviewTag(now = Date.now()): string | null {
  try {
    const raw = localStorage.getItem(PREVIEW_TAG_KEY);
    if (!raw) return null;
    const tag = JSON.parse(raw) as StoredTag;
    if (!tag?.token || new Date(tag.expiresAt).getTime() - MARGIN_MS <= now) return null;
    return tag.token;
  } catch {
    return null;
  }
}

export function setPreviewTag(tag: StoredTag): void {
  try {
    localStorage.setItem(PREVIEW_TAG_KEY, JSON.stringify(tag));
  } catch {
    // Storage blocked: links simply go untagged.
  }
}

export function clearPreviewTag(): void {
  try {
    localStorage.removeItem(PREVIEW_TAG_KEY);
  } catch {
    // nothing to clear
  }
}

/** Keeps a valid tag for the signed-in user. Mounted once in each signed-in shell. */
export function PreviewTagSync() {
  const { sendAuthGuardedRequest } = useContext(AuthContext);
  const sendRef = useRef(sendAuthGuardedRequest);
  sendRef.current = sendAuthGuardedRequest;

  useEffect(() => {
    let stopped = false;
    const fetchTag = () =>
      (sendRef.current(ApiMethod.POST, routes.internalTraffic.previewToken) as Promise<StoredTag>)
        .then((tag) => !stopped && tag?.token && setPreviewTag(tag))
        .catch(() => undefined);
    // A fresh one on every sign-in: the stored one may belong to whoever was signed in before.
    fetchTag();
    const id = setInterval(fetchTag, REFRESH_MS);
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, []);

  return null;
}

/** The link parameter a demo page's tracker reads the team tag from (pms-api-v2 demo-tracker.ts). */
export const DEMO_TEAM_PARAM = 'ardealy_team';

/**
 * A demo link (<slug>.ardealy.link) that marks the visit as ours: the demo
 * page remembers the tag and its visits are never counted as a prospect's.
 * Unchanged when there is no tag or the address cannot be read.
 */
export function withTeamTag(url: string, tag: string | null = getPreviewTag()): string {
  if (!tag || !url) return url;
  try {
    const u = new URL(url);
    u.searchParams.set(DEMO_TEAM_PARAM, tag);
    return u.toString();
  } catch {
    return url;
  }
}
